import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  HStack,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { fetchHolds, resolveHolds, discardHolds } from "./useNewData";

// PnP v2 "Unmatched imports" — rows the import could not link to a supplier
// (unknown vendor code AND unknown article code). Held, never guessed. Master
// picks the supplier -> the vendorcode mapping is saved and the rows import
// immediately. Shown on Imports -> New Data and in the login popup.
// Columns are deliberately ONLY what helps matching (Michael 2026-08-27):
// codes + description, data type, source, file, date, suppliers matched in the
// same file, and the held row count.
//
// compact (the login popup): ONE row per unknown VENDOR, not per article —
// 23 BOS article groups read as one line, and assigning it imports every held
// row of that vendor (the server replays vendor-wide). The per-article detail
// lives on the Imports -> New Data tab only (Michael 2026-08-28).

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "");

const groupKey = (g) => (g.ids || []).join(",");

// Collapse the per-(vendor, article, file) server groups into one row per
// vendor, keeping the article subgroups for the tab's detail rows. Groups with
// no vendor code cannot merge — they stay as their own single-article section.
function mergeByVendor(groups) {
  const byVendor = new Map();
  const out = [];
  for (const g of groups) {
    if (!g.vendor_code) {
      out.push({ ...g, article_count: 1, articles: [g], sample_descriptions: [g.article_description].filter(Boolean), filenames: [g.filename].filter(Boolean), sources: [g.source].filter(Boolean) });
      continue;
    }
    const k = `${g.retailer || ""}|${g.vendor_code}`;
    let m = byVendor.get(k);
    if (!m) {
      m = { ...g, ids: [], row_count: 0, article_count: 0, articles: [], sample_descriptions: [], filenames: [], sources: [] };
      byVendor.set(k, m);
      out.push(m);
    }
    m.ids = [...m.ids, ...(g.ids || [])];
    m.row_count += Number(g.row_count) || 0;
    m.article_count += 1;
    if (!m.vendor_name && g.vendor_name) m.vendor_name = g.vendor_name;
    m.articles.push(g);
    if (m.sample_descriptions.length < 3 && g.article_description) m.sample_descriptions.push(g.article_description);
    if (g.filename && !m.filenames.includes(g.filename)) m.filenames.push(g.filename);
    if (g.source && !m.sources.includes(g.source)) m.sources.push(g.source);
    if (g.first_seen && (!m.first_seen || g.first_seen < m.first_seen)) m.first_seen = g.first_seen;
  }
  return out.sort((a, b) => b.row_count - a.row_count);
}

const UnmatchedHolds = ({ suppliers = [], compact = false, onChanged }) => {
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState({}); // groupKey -> suppliercode
  const [busy, setBusy] = useState(null); // groupKey while resolving/discarding

  const supplierList = suppliers.map((s) => (typeof s === "string" ? s : s?.suppliercode)).filter(Boolean);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setGroups(await fetchHolds("held"));
    } catch (e) {
      // held section is an add-on — never break the page over it
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doResolve = async (g) => {
    const key = groupKey(g);
    const suppliercode = choice[key];
    if (!suppliercode) {
      toast({ title: "Pick a supplier first", status: "warning", duration: 2500, isClosable: true });
      return;
    }
    setBusy(key);
    try {
      const out = await resolveHolds({ ids: g.ids, vendor_code: g.vendor_code, suppliercode });
      toast({
        title: "Imported",
        description: `${out?.imported ?? 0} row(s) imported under ${suppliercode}`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.message || e?.message || "Resolve failed", status: "error", duration: 4500, isClosable: true });
    } finally {
      setBusy(null);
    }
  };

  const doDiscard = async (g) => {
    const key = groupKey(g);
    setBusy(key);
    try {
      await discardHolds(g.ids);
      toast({ title: "Discarded", description: `${g.row_count} row(s) discarded`, status: "info", duration: 3000, isClosable: true });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.message || e?.message || "Discard failed", status: "error", duration: 4500, isClosable: true });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <HStack py={4} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.500">
          Loading unmatched rows…
        </Text>
      </HStack>
    );
  }

  if (groups.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500" py={2}>
        No unmatched rows — every imported row linked to a supplier.
      </Text>
    );
  }

  // Popup: one line per vendor, few columns, fits the modal. Assign = whole vendor.
  if (compact) {
    const vendorGroups = mergeByVendor(groups);
    return (
      <Box>
        <Text fontSize="xs" color="gray.500" mb={2}>
          Assigning a supplier imports every held row for that vendor code and remembers the mapping. Full
          detail per article: Imports → New Data.
        </Text>
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Vendor</Th>
              <Th>Products</Th>
              <Th isNumeric>Rows</Th>
              <Th>Assign supplier</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {vendorGroups.map((g) => {
              const key = groupKey(g);
              return (
                <Tr key={key}>
                  <Td fontFamily="mono" whiteSpace="nowrap">
                    {g.retailer && (
                      <Badge colorScheme={g.retailer === "chk" ? "green" : g.retailer === "cli" ? "blue" : g.retailer === "dis" ? "pink" : "orange"} mr={1}>
                        {String(g.retailer).toUpperCase()}
                      </Badge>
                    )}
                    {g.vendor_code || "—"}
                    {g.vendor_name && (
                      <Text fontSize="xs" color="gray.500" fontFamily="body" fontWeight="normal" noOfLines={1} title={g.vendor_name}>
                        {g.vendor_name}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Text fontSize="xs" fontWeight="semibold">
                      {g.article_count > 1 ? `${g.article_count} articles` : g.article_code || "1 article"}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={2}>
                      {(g.sample_descriptions && g.sample_descriptions.length ? g.sample_descriptions : [g.article_description]).filter(Boolean).join(" · ")}
                    </Text>
                  </Td>
                  <Td isNumeric>{g.row_count}</Td>
                  <Td>
                    <Select
                      size="xs"
                      placeholder="Supplier…"
                      value={choice[key] || ""}
                      onChange={(e) => setChoice((prev) => ({ ...prev, [key]: e.target.value }))}
                      minW="130px"
                    >
                      {supplierList.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Button size="xs" colorScheme="blue" mr={1} onClick={() => doResolve(g)} isLoading={busy === key} isDisabled={!choice[key]}>
                      Import
                    </Button>
                    <Button size="xs" variant="outline" colorScheme="red" onClick={() => doDiscard(g)} isDisabled={busy === key}>
                      Discard
                    </Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  }

  // Tab: one SECTION per vendor — a header row carrying the single supplier
  // picker + Import/Discard for the whole vendor (the server replays
  // vendor-wide), then the per-article detail rows beneath it, without controls.
  const sections = mergeByVendor(groups);
  return (
    <Box overflowX="auto">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Vendor</Th>
            <Th>Articles</Th>
            <Th>Source</Th>
            <Th>File(s)</Th>
            <Th>Date</Th>
            <Th>Suppliers in file</Th>
            <Th isNumeric>Rows</Th>
            <Th>Assign supplier</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {sections.map((v) => {
            const key = groupKey(v);
            return (
              <React.Fragment key={key}>
                <Tr bg="blackAlpha.50">
                  <Td fontFamily="mono" fontWeight="bold" whiteSpace="nowrap">
                    {v.retailer && (
                      <Badge colorScheme={v.retailer === "chk" ? "green" : v.retailer === "cli" ? "blue" : v.retailer === "dis" ? "pink" : "orange"} mr={1}>
                        {String(v.retailer).toUpperCase()}
                      </Badge>
                    )}
                    {v.vendor_code || "(no vendor code)"}
                    {v.vendor_name && (
                      <Text fontSize="xs" color="gray.500" fontFamily="body" fontWeight="normal" noOfLines={1} title={v.vendor_name}>
                        {v.vendor_name}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Text fontSize="sm" fontWeight="semibold">
                      {v.article_count} article{v.article_count === 1 ? "" : "s"}
                    </Text>
                  </Td>
                  <Td>
                    {v.sources.map((s) => (
                      <Badge key={s} colorScheme={s === "edi" ? "purple" : "teal"} mr={1}>
                        {s === "edi" ? "EDI" : `Manual${v.uploaded_by ? ` (user ${v.uploaded_by})` : ""}`}
                      </Badge>
                    ))}
                  </Td>
                  <Td maxW="220px">
                    <Text fontSize="xs" noOfLines={2}>
                      {v.filenames[0]}
                      {v.filenames.length > 1 ? ` +${v.filenames.length - 1} more` : ""}
                    </Text>
                  </Td>
                  <Td>{fmtDate(v.first_seen)}</Td>
                  <Td maxW="180px">
                    <Text fontSize="xs">{v.file_suppliers || "none matched"}</Text>
                  </Td>
                  <Td isNumeric fontWeight="bold">
                    {v.row_count}
                  </Td>
                  <Td>
                    <Select
                      size="xs"
                      placeholder="Supplier…"
                      value={choice[key] || ""}
                      onChange={(e) => setChoice((prev) => ({ ...prev, [key]: e.target.value }))}
                      minW="140px"
                    >
                      {supplierList.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <Button size="xs" colorScheme="blue" mr={1} onClick={() => doResolve(v)} isLoading={busy === key} isDisabled={!choice[key]}>
                      Import all
                    </Button>
                    <Button size="xs" variant="outline" colorScheme="red" onClick={() => doDiscard(v)} isDisabled={busy === key}>
                      Discard all
                    </Button>
                  </Td>
                </Tr>
                {v.articles.map((g) => (
                  <Tr key={groupKey(g)}>
                    <Td />
                    <Td>
                      <Text fontFamily="mono" fontSize="xs">
                        {g.article_code || "—"}
                      </Text>
                      {g.article_description && (
                        <Text fontSize="xs" color="gray.500">
                          {g.article_description}
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme="cyan" variant="subtle">
                        {g.data_type}
                      </Badge>
                    </Td>
                    <Td colSpan={3} />
                    <Td isNumeric fontSize="xs">
                      {g.row_count}
                    </Td>
                    <Td colSpan={2} />
                  </Tr>
                ))}
              </React.Fragment>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default UnmatchedHolds;
